/**
 * Pure geometry for the upload grid's rubber-band (marquee) selection.
 * No DOM, no React — unit-tested in marquee.test.ts. UploadScreen feeds it the
 * live card rects (getBoundingClientRect) and the drag rectangle; it returns the
 * ids of the cards the rectangle touches.
 *
 * All boxes are viewport-space { left, top, right, bottom } so a marquee built
 * from pointer clientX/clientY hit-tests directly against card client rects.
 */

export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface MarqueeItem {
  id: string;
  box: Box;
}

/** Build a normalized box from two drag points (order-independent). */
export function boxFromPoints(x0: number, y0: number, x1: number, y1: number): Box {
  return {
    left: Math.min(x0, x1),
    top: Math.min(y0, y1),
    right: Math.max(x0, x1),
    bottom: Math.max(y0, y1),
  };
}

/** True when two axis-aligned boxes overlap (touching edges do NOT count). */
export function intersects(a: Box, b: Box): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/**
 * A degenerate (zero/negative area) box — e.g. the rect of a tile whose layout
 * is currently skipped by content-visibility. These must never hit-test: a
 * collapsed rect would otherwise act as a stray point and select tiles the
 * visible marquee never touched.
 */
export function isDegenerateBox(b: Box): boolean {
  return b.right <= b.left || b.bottom <= b.top;
}

/** Longer diagonal-agnostic movement past which a press becomes a drag. */
export const DRAG_THRESHOLD_PX = 5;

/** True once the pointer has moved far enough from its start to count as a drag. */
export function passedThreshold(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  threshold = DRAG_THRESHOLD_PX,
): boolean {
  return Math.hypot(x1 - x0, y1 - y0) >= threshold;
}

/**
 * Ids of every item whose box intersects the marquee, in the items' given order.
 * Pure 2D geometric intersection — a tall narrow marquee only selects items it
 * also overlaps horizontally (never an index range). Degenerate item boxes are
 * skipped (see isDegenerateBox).
 */
export function hitTest(marquee: Box, items: readonly MarqueeItem[]): string[] {
  const out: string[] = [];
  for (const item of items) {
    if (isDegenerateBox(item.box)) continue;
    if (intersects(marquee, item.box)) out.push(item.id);
  }
  return out;
}

/**
 * Resolve the selection a drag should produce: the additive base (existing
 * selection when Shift is held, else empty) unioned with the marquee's hits.
 */
export function marqueeSelection(
  base: Iterable<string>,
  marquee: Box,
  items: readonly MarqueeItem[],
): Set<string> {
  const next = new Set(base);
  for (const id of hitTest(marquee, items)) next.add(id);
  return next;
}

/**
 * Inclusive id range between an anchor and a target within an ordered id list
 * (Shift-click range select). Returns [] when either id is missing.
 */
export function rangeBetween(order: readonly string[], anchorId: string, targetId: string): string[] {
  const a = order.indexOf(anchorId);
  const b = order.indexOf(targetId);
  if (a === -1 || b === -1) return [];
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  return order.slice(lo, hi + 1);
}
